import * as SelectPrimitive from '@rn-primitives/select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react-native';
import * as React from 'react';
import { Platform, ScrollView, Text, View, StyleProp, ViewStyle, TextStyle } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { FadeIn, FadeOut } from 'react-native-reanimated';
import { FullWindowOverlay as RNFullWindowOverlay } from 'react-native-screens';
import { NativeOnlyAnimatedView } from '@/components/ui/native-only-animated-view';
import { useTheme } from '@/lib/styling';
import { Theme } from '@/theme';

const styles = StyleSheet.create(() => ({
  itemIndicator: {
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
    width: 14,
  },
  itemRow: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  viewport: {
    padding: 4,
  },
}));

type Option = SelectPrimitive.Option;
export const EMPTY_SELECT_VALUE = '__appselect_empty__';

type SelectProps = Omit<SelectPrimitive.RootProps, 'value' | 'defaultValue' | 'onValueChange'> & {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  options?: { value: string; label: string }[];
};

const SelectOptionsContext = React.createContext<{ 
  registerOption: (value: string, label: string) => void;
  unregisterOption: (value: string) => void;
}>({
  registerOption: () => {},
  unregisterOption: () => {},
});

function Select({
  value,
  defaultValue,
  onValueChange,
  options: optionsProp,
  children,
  ...props
}: SelectProps) {
  const [options, setOptions] = React.useState<Map<string, string>>(() => new Map());

  React.useEffect(() => {
    if (!optionsProp) {
      return;
    }
    setOptions(new Map(optionsProp.map((option) => [option.value, option.label])));
  }, [optionsProp]);

  const registerOption = React.useCallback((optionValue: string, label: string) => {
    setOptions((prevOptions) => {
      const nextOptions = new Map(prevOptions);
      nextOptions.set(optionValue, label);
      return nextOptions;
    });
  }, []);

  const unregisterOption = React.useCallback((optionValue: string) => {
    setOptions((prevOptions) => {
      if (!prevOptions.has(optionValue)) {
        return prevOptions;
      }
      const nextOptions = new Map(prevOptions);
      nextOptions.delete(optionValue);
      return nextOptions;
    });
  }, []);

  const selectedOption =
    value === undefined ? undefined : { value, label: options.get(value) ?? value };
  const defaultOption =
    defaultValue === undefined
      ? undefined
      : { value: defaultValue, label: options.get(defaultValue) ?? defaultValue };

  return (
    <SelectOptionsContext.Provider value={{ registerOption, unregisterOption }}>
      <SelectPrimitive.Root
        value={selectedOption}
        defaultValue={defaultOption}
        onValueChange={(option) => onValueChange?.(option?.value ?? '')}
        {...props}
      >
        {children}
      </SelectPrimitive.Root>
    </SelectOptionsContext.Provider>
  );
}

const SelectGroup = SelectPrimitive.Group;

function SelectValue({
  ref,
  className,
  style,
  placeholder = 'Select...', 
  ...props
}: Omit<SelectPrimitive.ValueProps, 'placeholder'> &
  React.RefAttributes<SelectPrimitive.ValueRef> & {
    className?: string;
    style?: StyleProp<TextStyle>;
    placeholder?: string;
  }) {
  const { value } = SelectPrimitive.useRootContext();
  const theme = useTheme<Theme>();
  const hasSelection = Boolean(value) &&
    !(typeof value === 'object' && value !== null && 'value' in value && value.value === EMPTY_SELECT_VALUE);
  
  // Extract numberOfLines to prevent passing it to DOM element on web
  const { numberOfLines, ...otherProps } = props;

  const flattenedStyle = StyleSheet.flatten([
    {
      color: hasSelection ? theme.colors.text : theme.colors.muted,
      fontSize: 15,
      lineHeight: 20,
      fontFamily: theme.typography.fontFamily.body,
    },
    style,
    // Add web truncation styles if numberOfLines is 1
    Platform.OS === 'web' && numberOfLines === 1
      ? {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'block', // Ensure block/inline-block for overflow to work
          maxWidth: '100%',
        }
      : undefined,
  ]) as TextStyle;

  const selectedLabel =
    value && typeof value === 'object' && 'label' in value
      ? value.label
      : typeof value === 'string'
        ? value
        : undefined;
  const visibleLabel = selectedLabel === EMPTY_SELECT_VALUE ? undefined : selectedLabel;

  return (
    <Text
      ref={ref as React.Ref<Text>}
      style={flattenedStyle}
      {...(numberOfLines === undefined ? {} : { numberOfLines })}
      {...otherProps}
    >
      {visibleLabel || placeholder}
    </Text>
  );
}

function SelectTrigger({
  className,
  surfaceStyle,
  style,
  children,
  size = 'default',
  ...props
}: SelectPrimitive.TriggerProps &
  React.RefAttributes<SelectPrimitive.TriggerRef> & {
    children?: React.ReactNode;
    size?: 'default' | 'sm';
    className?: string;
    surfaceStyle?: StyleProp<ViewStyle>;
    style?: StyleProp<ViewStyle>;
  }) {
  const theme = useTheme<Theme>();
  const triggerRef = React.useRef<SelectPrimitive.TriggerRef>(null);
  
  const handleTouchStart = React.useCallback(
    (event: any) => {
      if (Platform.OS === 'web' && !props.disabled) {
        triggerRef.current?.open?.();
      }
      props.onTouchStart?.(event);
    },
    [props],
  );

  const flattenedStyle = StyleSheet.flatten([
    {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadii.m,
      paddingHorizontal: theme.spacing.m,
      paddingVertical: theme.spacing.s,
      height: size === 'sm' ? 32 : 40,
      opacity: props.disabled ? 0.5 : 1,
      shadowColor: 'transparent',
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0,
      shadowRadius: 0,
      elevation: 0,
    } as ViewStyle,
    ...(Array.isArray(surfaceStyle) ? surfaceStyle : [surfaceStyle]),
    ...(Array.isArray(style) ? style : [style]),
  ]);

  return (
    <SelectPrimitive.Trigger
      ref={triggerRef}
      style={flattenedStyle}
      {...(Platform.OS === 'web' ? { onTouchStart: handleTouchStart } : {})}
      {...props}>
      <>{children}</>
      <ChevronDown size={16} color={theme.colors.press} aria-hidden={true} />
    </SelectPrimitive.Trigger>
  );
}

const FullWindowOverlay = Platform.OS === 'ios' ? RNFullWindowOverlay : React.Fragment;

function SelectContent({
  className,
  children,
  position = 'popper',
  portalHost,
  style,
  ...props
}: SelectPrimitive.ContentProps & {
  className?: string;
  portalHost?: string;
  style?: StyleProp<ViewStyle>;
}) {
  const theme = useTheme<Theme>();

  return (
    <SelectPrimitive.Portal {...(portalHost ? { hostName: portalHost } : {})}>
      <FullWindowOverlay>
        <SelectPrimitive.Overlay
          style={Platform.select({ native: StyleSheet.absoluteFill })}
        >
          <NativeOnlyAnimatedView entering={FadeIn} exiting={FadeOut}>
            <SelectPrimitive.Content
              position={position}
              {...props}
              style={StyleSheet.flatten([
                {
                  backgroundColor: theme.colors.surface,
                  borderWidth: 1,
                  borderColor: theme.colors.border,
                  borderRadius: theme.borderRadii.m,
                  shadowColor: 'transparent',
                  shadowOffset: { width: 0, height: 0 },
                  shadowOpacity: 0,
                  shadowRadius: 0,
                  elevation: 2,
                  minWidth: 128,
                  overflow: 'hidden',
                  zIndex: 9999, // Ensure it's on top
                  position: 'absolute', // Force absolute positioning to ensure z-index works on web
                },
                Platform.OS !== 'web' && { padding: 4 },
                style
              ])}
            >
              <SelectPrimitive.Viewport style={styles.viewport}>
                {children}
              </SelectPrimitive.Viewport>
            </SelectPrimitive.Content>
          </NativeOnlyAnimatedView>
        </SelectPrimitive.Overlay>
      </FullWindowOverlay>
    </SelectPrimitive.Portal>
  );
}


function SelectLabel({
  className,
  style,
  ...props
}: SelectPrimitive.LabelProps & React.RefAttributes<SelectPrimitive.LabelRef> & { className?: string, style?: StyleProp<TextStyle> }) {
  const theme = useTheme<Theme>();
  return (
    <SelectPrimitive.Label
      style={StyleSheet.flatten([
        {
          paddingHorizontal: theme.spacing.s,
          paddingVertical: theme.spacing.s,
          fontSize: 12,
          fontFamily: theme.typography.fontFamily.mono,
          fontWeight: '700',
          letterSpacing: 0.8,
          textTransform: 'uppercase',
          color: theme.colors.pressMuted,
        },
        style
      ])}
      {...props}
    />
  );
}

function SelectItem({
  style,
  children,
  label,
  ...props
}: Omit<SelectPrimitive.ItemProps, 'label'> &
  React.RefAttributes<SelectPrimitive.ItemRef> & { label?: string, className?: string, style?: StyleProp<ViewStyle> }) {
  const theme = useTheme<Theme>();
  const { registerOption, unregisterOption } = React.useContext(SelectOptionsContext);
  const resolvedLabel = label ?? (typeof children === 'string' ? children : String(children));

  React.useEffect(() => {
    registerOption(props.value, resolvedLabel);
    return () => unregisterOption(props.value);
  }, [props.value, registerOption, resolvedLabel, unregisterOption]);

  return (
    <SelectPrimitive.Item
      label={resolvedLabel}
      style={StyleSheet.flatten([
        {
          borderRadius: theme.borderRadii.none,
          paddingVertical: theme.spacing.s,
          paddingHorizontal: theme.spacing.s,
          width: '100%',
        },
        props.disabled && { opacity: 0.5 },
        style,
      ])}
      {...props}>
      <View style={styles.itemRow}>
        <View style={styles.itemIndicator}>
          <SelectPrimitive.ItemIndicator>
            <Check size={14} color={theme.colors.press} />
          </SelectPrimitive.ItemIndicator>
        </View>
        <SelectPrimitive.ItemText 
          style={StyleSheet.flatten([
            { fontSize: 15, color: theme.colors.text, fontFamily: theme.typography.fontFamily.body },
            Platform.OS === 'web' && {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              maxWidth: '100%'
            } as any
          ])} 
        />
      </View>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  style,
  ...props
}: SelectPrimitive.SeparatorProps & React.RefAttributes<SelectPrimitive.SeparatorRef> & { className?: string, style?: StyleProp<ViewStyle> }) {
  const theme = useTheme<Theme>();
  return (
    <SelectPrimitive.Separator
      style={StyleSheet.flatten([
        {
          height: 1,
          backgroundColor: theme.colors.border,
          marginVertical: theme.spacing.xs,
        },
        style
      ])}
      {...props}
    />
  );
}

/**
 * @platform Web only
 * Returns null on native platforms
 */
function SelectScrollUpButton({
  className,
  style,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton> & { className?: string, style?: StyleProp<ViewStyle> }) {
  if (Platform.OS !== 'web') {
    return null;
  }
  return (
    <SelectPrimitive.ScrollUpButton
      style={StyleSheet.flatten([
        {
          alignItems: 'center',
          justifyContent: 'center',
          paddingVertical: 4,
        },
        style
      ]) as any}
      {...props}>
      <ChevronUp size={14} color="black" />
    </SelectPrimitive.ScrollUpButton>
  );
}

/**
 * @platform Web only
 * Returns null on native platforms
 */
function SelectScrollDownButton({
  className,
  style,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton> & { className?: string, style?: StyleProp<ViewStyle> }) {
  if (Platform.OS !== 'web') {
    return null;
  }
  return (
    <SelectPrimitive.ScrollDownButton
      style={StyleSheet.flatten([
        {
          alignItems: 'center',
          justifyContent: 'center',
          paddingVertical: 4,
        },
        style
      ]) as any}
      {...props}>
      <ChevronDown size={14} color="black" />
    </SelectPrimitive.ScrollDownButton>
  );
}

/**
 * A scrollable view for select options with a max height.
 */
function NativeSelectScrollView({ className, style, ...props }: React.ComponentProps<typeof ScrollView> & { className?: string }) {
  return <ScrollView style={StyleSheet.flatten([{ maxHeight: 240 }, style])} {...props} />;
}

export { 
  NativeSelectScrollView,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  type Option,
};
